import CheckIcon from "@mui/icons-material/Check";
import {
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import {
  getGrantedScopes,
  getScopeResource,
} from "Components/Sso/ssoResources";
import { useSignOutAndReturn } from "Hooks/useSignOutAndReturn";
import { useLoggedInUser } from "Hooks/useLoggedInUser";
import type { SsoAuthorizationRequestDto } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";

const RESOURCES = Resources.Sso.Consent;

type Props = {
  request: SsoAuthorizationRequestDto;
  isDeciding: boolean;
  onDecide: (isApproved: boolean) => void;
};

export const SsoConsentCard = ({ request, isDeciding, onDecide }: Props) => {
  const { t } = useResource();
  const user = useLoggedInUser();
  const switchAccount = useSignOutAndReturn();
  const scopes = getGrantedScopes(request.scopes);
  const name = `${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim();
  const signedInAs = name ? `${name} (${user?.login})` : user?.login;

  return (
    <Paper>
      <Stack gap={3} padding={3}>
        <Typography variant="h5">{request.clientDisplayName}</Typography>
        <Typography>
          {t(RESOURCES.Intro, { app: request.clientDisplayName })}
        </Typography>
        {/* Consent is given by whoever is signed in here, so they must see who that is before allowing. */}
        {signedInAs && (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={1}
          >
            <Typography variant="body2" color="text.secondary">
              {t(RESOURCES.SignedInAs, { who: signedInAs })}
            </Typography>
            <Button size="small" disabled={isDeciding} onClick={switchAccount}>
              {t(RESOURCES.UseAnotherAccount)}
            </Button>
          </Stack>
        )}
        <Stack gap={1}>
          <Typography fontWeight="bold">
            {t(RESOURCES.PermissionsHeading)}
          </Typography>
          <List dense disablePadding>
            {scopes.map((scope) => {
              const resource = getScopeResource(scope);

              return (
                <ListItem key={scope} disableGutters>
                  <ListItemIcon>
                    <CheckIcon color="success" />
                  </ListItemIcon>
                  <ListItemText primary={resource ? t(resource) : scope} />
                </ListItem>
              );
            })}
          </List>
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {t(RESOURCES.RevokeHint)}
        </Typography>
        <Stack direction="row" gap={2} justifyContent="flex-end">
          <Button
            variant="outlined"
            disabled={isDeciding}
            onClick={() => onDecide(false)}
          >
            {t(RESOURCES.Deny)}
          </Button>
          <Button
            variant="contained"
            disabled={isDeciding}
            onClick={() => onDecide(true)}
          >
            {t(RESOURCES.Allow)}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
};
